import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth } from '../services/firebase'; // 🟢 FIXED: Only one level up
import { onAuthStateChanged } from 'firebase/auth';

export interface CartItem {
  id: string; 
  cartId: string; 
  name: string;
  price: number;
  quantity: number;
  image: string;         
  brand: string;         
  selectedSize: string;  
  selectedColor: string; 
  businessId: string;    
  category: string;      
  [key: string]: any;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: any) => void;
  removeFromCart: (cartId: string) => void;
  updateQuantity: (cartId: string, delta: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  // 1. Listen for who is logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        const savedCart = localStorage.getItem(`scoop_cart_${user.uid}`);
        setCartItems(savedCart ? JSON.parse(savedCart) : []);
      } else {
        setUserId('guest');
        const savedCart = localStorage.getItem('scoop_cart_guest');
        setCartItems(savedCart ? JSON.parse(savedCart) : []);
      }
    });

    return () => unsubscribe();
  }, []);

  // 2. Save to the specific user's storage key
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`scoop_cart_${userId}`, JSON.stringify(cartItems));
    }
  }, [cartItems, userId]);

  const addToCart = (item: any) => {
    setCartItems(prev => {
      const existing = prev.find(i => i.id === item.id && i.selectedSize === item.selectedSize && i.selectedColor === item.selectedColor);
      if (existing) {
        return prev.map(i => i.cartId === existing.cartId ? { ...i, quantity: (i.quantity || 1) + 1 } : i);
      }
      const newItem = { ...item, cartId: `${item.id}-${Date.now()}`, quantity: item.quantity || 1 };
      return [...prev, newItem];
    });
  };

  const removeFromCart = (cartId: string) => {
    setCartItems(prev => prev.filter(i => i.cartId !== cartId));
  };

  const updateQuantity = (cartId: string, delta: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.cartId === cartId) {
        const newQuantity = Math.max(1, (item.quantity || 1) + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };

  const clearCart = () => setCartItems([]);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};